import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import Breadcrumbs from "../BreadCrum/BreadCrum";
import PageHeadingSection from "../pageheading/PageHeadingSection";
import CommonTable from "../table/ComonTable";
import DeleteModal from "../commonmodal/DeleteModal";
import { emergencyApi, readSession, apiMessage } from "./emergencyApi";

const EmergencyTypeList = () => {
    const navigate = useNavigate();
    const { user } = useSelector((state) => state.auth);
    const { siteId } = readSession(user);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pendingDelete, setPendingDelete] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const response = await emergencyApi.getTypes(siteId);
            const list = response.data?.data || response.data?.Data || [];
            setRows(Array.isArray(list) ? list.map((item) => ({
                ...item,
                id: item.id,
                subTypeNames: (item.sub_types || []).map((sub) => sub.name).filter(Boolean).join(", "),
                recipientCount: Array.isArray(item.recipients) ? item.recipients.length : 0
            })) : []);
        } catch (error) {
            toast.error(apiMessage(error, "Failed to load emergency types"));
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [siteId]);

    useEffect(() => { load(); }, [load]);

    const confirmDelete = async () => {
        if (!pendingDelete) return;
        try {
            const response = await emergencyApi.deleteType(pendingDelete.id);
            if (response.data?.isSuccess === false) {
                toast.error(response.data?.message || "Failed to delete type");
                return;
            }
            toast.success("Emergency type deleted");
            setPendingDelete(null);
            load();
        } catch (error) {
            toast.error(apiMessage(error, "Failed to delete type"));
        }
    };

    return (
        <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto pt-4 scrollbar-hide">
            <Breadcrumbs items={["Emergency Services", "Types"]} />
            <PageHeadingSection
                mainHeading="Emergency types"
                subHeading="Map the staff who should receive a full-screen alert when this type is raised."
                primaryBtnText="Add type"
                primaryBtnIcon={faPlus}
                PrimaryOnClick={() => navigate("/common/emergency-services/types/add")}
            />
            <div className="mt-4">
                <CommonTable
                    loading={loading}
                    headers={[
                        { label: "NAME", key: "name" },
                        { label: "SUB TYPES", key: "subTypeNames" },
                        { label: "RECIPIENTS", key: "recipientCount" },
                        { label: "ACTIONS", key: "actions" }
                    ]}
                    data={rows}
                    renderCell={(key, row) => {
                        if (key === "name") return row.name || "—";
                        if (key === "recipientCount") return String(row.recipientCount ?? 0);
                        return row.subTypeNames || "—";
                    }}
                    onEdit={(row) => navigate(`/common/emergency-services/types/add/${row.id}`, { state: row })}
                    onDelete={(row) => setPendingDelete(row)}
                />
            </div>
            <DeleteModal
                isOpen={!!pendingDelete}
                onClose={() => setPendingDelete(null)}
                onConfirm={confirmDelete}
                title="Delete emergency type"
                description={`Delete ${pendingDelete?.name || "this type"}?`}
            />
        </div>
    );
};

export default EmergencyTypeList;
